import { useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import { BarChart3, ChevronDown, Download } from 'lucide-react';
import DesktopPeriodSelector from '../common/DesktopPeriodSelector';

/**
 * Faixa de filtros dos relatórios SEM card (`filter-bar`, rev. 2026-10-23 —
 * ui-ux/prototipo-relatorio-*.html). Substitui o `FilterToolbar` nos relatórios
 * de Vendas, OS, Produtos, Estoque e Fluxo de Caixa (desktop).
 *
 * Período = `DesktopPeriodSelector` (chips + campo único com calendário).
 * Chips e calendário SÓ preenchem o período (`onPeriodChange`); a geração é
 * sempre explícita via "Gerar relatório" (`onGenerate`).
 *
 * Props:
 * - `start`/`end`/`onPeriodChange(start, end)`  período (dayjs).
 * - `children`      filtros extras (campos compactos depois do período).
 * - `extraKey`      string que muda quando um filtro extra "que exige gerar" muda
 *                   (entra no cálculo do ponto âmbar).
 * - `onGenerate`, `loading`, `generateLabel`.
 * - `onClear`       handler do "Limpar"; `clearGenerates` = o Limpar já regenera
 *                   (então o estado "aplicado" volta aos valores iniciais).
 * - `exportItems`   [{ label, description?, onClick, disabled? }] — menu "Exportar".
 * - `showPeriod`   (padrão true) false = relatório sem período (ex.: Checklist);
 *                   `start`/`end`/`onPeriodChange` ficam dispensados.
 * - `showGenerate` (padrão true) false = oculta "Gerar relatório" e o ponto âmbar
 *                   (relatório que carrega sozinho ao mudar o filtro, ex.: Inventário).
 *
 * Ponto âmbar ("filtro alterado ainda não aplicado"): comparado internamente
 * com a assinatura (período + extraKey) da última geração (ou da montagem).
 */
const signatureOf = (start, end, extraKey) => (
    `${start?.isValid?.() ? start.format('YYYY-MM-DD') : ''}|${end?.isValid?.() ? end.format('YYYY-MM-DD') : ''}|${extraKey || ''}`
);

export default function ReportFilterBar({
    start,
    end,
    onPeriodChange,
    children,
    extraKey = '',
    onGenerate,
    loading = false,
    generateLabel = 'Gerar relatório',
    onClear,
    clearGenerates = false,
    exportItems = [],
    showPeriod = true,
    showGenerate = true,
}) {
    const signature = signatureOf(start, end, extraKey);
    const initialRef = useRef(signature);
    const [applied, setApplied] = useState(signature);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const isDirty = signature !== applied;
    const hasExport = exportItems.length > 0;
    const exportDisabled = !hasExport || exportItems.every((i) => i.disabled);

    const handleGenerate = () => {
        setApplied(signature);
        if (onGenerate) onGenerate();
    };

    const handleClear = () => {
        if (clearGenerates) setApplied(initialRef.current);
        if (onClear) onClear();
    };

    const closeMenu = () => setMenuAnchor(null);

    return (
        <Box
            role="region"
            aria-label="Filtros do relatório"
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                gap: '14px 22px',
                pb: '18px',
                mb: '20px',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '@media (max-width:760px)': {
                    gap: '14px',
                    '& .rfb-group, & .rfb-extras > *': { width: '100%', maxWidth: 'none', flex: '1 1 100%' },
                    '& .rfb-group > div': { width: '100%' },
                    '& button[aria-label="Selecionar período"]': { width: '100%' },
                    '& .rfb-extras': { width: '100%' },
                    '& .rfb-actions': { width: '100%', ml: 0, pt: '4px' },
                    '& .rfb-gen': { flex: '1 1 auto' },
                },
            }}
        >
            {showPeriod && (
                <Box className="rfb-group" sx={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
                    <Typography component="span" sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                        Período
                    </Typography>
                    <DesktopPeriodSelector
                        start={start}
                        end={end}
                        onChange={onPeriodChange}
                        showRefresh={false}
                        dualMonth
                        align="start"
                    />
                </Box>
            )}

            {children && (
                <Box className="rfb-extras" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '14px 22px' }}>
                    {children}
                </Box>
            )}

            <Box
                className="rfb-actions"
                sx={{ ml: 'auto', pr: '4px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}
            >
                {onClear && (
                    <Button
                        variant="text"
                        onClick={handleClear}
                        title="Voltar aos filtros padrão"
                        sx={{
                            height: 40,
                            px: '10px',
                            color: 'text.secondary',
                            fontSize: '12.5px',
                            fontWeight: 600,
                            '&:hover': { color: 'primary.main', textDecoration: 'underline', bgcolor: 'transparent' },
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
                        }}
                    >
                        Limpar
                    </Button>
                )}

                {hasExport && (
                    <>
                        <Button
                            variant="outlined"
                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                            disabled={exportDisabled}
                            aria-haspopup="menu"
                            aria-expanded={Boolean(menuAnchor)}
                            aria-controls={menuAnchor ? 'report-export-menu' : undefined}
                            title="Exportar dados do relatório"
                            startIcon={<Download size={15} strokeWidth={2} />}
                            endIcon={<ChevronDown size={14} strokeWidth={2} />}
                            sx={{
                                height: 40,
                                px: '14px',
                                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
                            }}
                        >
                            Exportar
                        </Button>
                        <Menu
                            id="report-export-menu"
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={closeMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{
                                list: { role: 'menu', 'aria-label': 'Exportar' },
                                paper: { sx: { mt: 1, minWidth: 260, borderRadius: '12px', p: '6px' } },
                            }}
                        >
                            {exportItems.map((item) => (
                                <MenuItem
                                    key={item.label}
                                    disabled={item.disabled}
                                    onClick={() => {
                                        closeMenu();
                                        item.onClick();
                                    }}
                                    sx={{ borderRadius: '8px', gap: '2px', py: '9px', '&:hover, &.Mui-focusVisible': { bgcolor: 'background.subtle', color: 'primary.main' } }}
                                >
                                    <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
                                        <Download size={15} strokeWidth={2} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.label}
                                        secondary={item.description}
                                        primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }}
                                        secondaryTypographyProps={{ fontSize: '11.5px' }}
                                    />
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )}

                {showGenerate && (
                <Tooltip title={isDirty && !loading ? 'Filtros alterados — clique em Gerar' : ''} arrow>
                    <Box component="span" className="rfb-gen" sx={{ position: 'relative', display: 'inline-flex' }}>
                        <Button
                            variant="contained"
                            onClick={handleGenerate}
                            disabled={loading}
                            aria-busy={loading}
                            aria-label={isDirty && !loading ? `${generateLabel} (filtros alterados)` : undefined}
                            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <BarChart3 size={15} strokeWidth={2} />}
                            sx={{
                                height: 40,
                                px: '18px',
                                flex: '1 1 auto',
                                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
                            }}
                        >
                            {loading ? 'Gerando...' : generateLabel}
                        </Button>
                        {isDirty && !loading && (
                            <Box
                                aria-hidden
                                sx={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: 'warning.main',
                                    border: '2px solid',
                                    borderColor: 'background.default',
                                    pointerEvents: 'none',
                                }}
                            />
                        )}
                    </Box>
                </Tooltip>
                )}
            </Box>
        </Box>
    );
}
